import { Controller } from "@hotwired/stimulus"

// Controlador para upload chunked de video/audio a Mediastream.
// Replica: useUploadToMediaStream.tsx + VodWrap.tsx + VodSettings.tsx
//
// Flujo:
//   1. Usuario selecciona archivo (selectFile)
//   2. Usuario clickea "Subir archivo" (upload)
//   3. POST create_media → obtiene vod_media_id
//   4. POST upload_url → obtiene URL del servidor de chunks
//   5. Loop: file.slice() en chunks de 1MB, POST secuencial a Mediastream
//   6. Tras cada chunk: PATCH update_percent al backend
//   7. Al completar: cierra modal, muestra estado "procesando"
//   8. Polling cada 60s: GET check_ready → cuando ready, recarga token + muestra player
//
// Uso HTML:
//   <div data-controller="manage--vod-upload"
//        data-manage--vod-upload-create-media-url-value="/manage/x/p/1/vod/create_media"
//        data-manage--vod-upload-upload-url-value="/manage/x/p/1/vod/upload_url"
//        data-manage--vod-upload-update-percent-url-value="/manage/x/p/1/vod/update_percent"
//        data-manage--vod-upload-check-ready-url-value="/manage/x/p/1/vod/check_ready"
//        data-manage--vod-upload-token-url-value="/manage/x/p/1/vod/token"
//        data-manage--vod-upload-media-type-value="video"
//        data-manage--vod-upload-vod-media-id-value=""
//        data-manage--vod-upload-vod-ready-value="false"
//        data-manage--vod-upload-vod-media-ready-value="false">
//
export default class extends Controller {
  static targets = [
    "fileInput",        // <input type="file">
    "fileName",         // Label con nombre del archivo
    "progressSection",  // Contenedor de la barra de progreso
    "progressBar",      // Barra de progreso (inner fill)
    "progressText",     // Texto "%"
    "formSection",      // Seccion del form (file + submit)
    "errorSection",     // Seccion de error
    "submitBtn",        // Boton submit
    // Estados del partial principal
    "noMediaState",     // Estado: sin media
    "processingState",  // Estado: procesando
    "playerState",      // Estado: player listo
  ]

  static values = {
    createMediaUrl: String,
    uploadUrl: String,
    updatePercentUrl: String,
    checkReadyUrl: String,
    tokenUrl: String,
    playerUrl: String,
    mediaType: { type: String, default: "video" },
    vodMediaId: { type: String, default: "" },
    vodReady: { type: Boolean, default: false },
    vodMediaReady: { type: Boolean, default: false },
    chunkSize: { type: Number, default: 1000000 },
    csrfToken: String,
    errorFileRequired: { type: String, default: "Debes seleccionar un archivo" },
    errorInvalidFormat: { type: String, default: "El archivo no es un formato valido" },
  }

  connect() {
    this.file = null
    this.pollTimer = null

    // Si ya esta en estado "procesando" (vod subido pero no ready),
    // iniciar polling que auto-recarga el partial cuando Mediastream este listo.
    if (this.vodReadyValue && this.vodMediaIdValue && !this.vodMediaReadyValue) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  // --- Actions ---

  // Previene que clicks en botones del player de Mediastream (que no tienen
  // type="button") burbujeen como submit al form padre de configuracion.
  stopPlayerSubmit(event) {
    const target = event.target.closest("button")
    if (target && target.type !== "button") {
      event.preventDefault()
    }
  }

  selectFile(event) {
    this.file = event.target.files[0]
    console.log("[vod-upload] selectFile:", this.file?.name, this.file?.type, this.file?.size)
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = this.file
        ? this.file.name
        : this.fileNameTarget.dataset.placeholder
    }
  }

  async upload(event) {
    // No preventDefault — es un click en button type="button", no un form submit
    console.log("[vod-upload] upload() called", { file: this.file, mediaType: this.mediaTypeValue })

    // Validaciones (replica useUploadToMediaStream.upload)
    if (!this.file) {
      this.showError(this.errorFileRequiredValue)
      return
    }

    const expectedMime = this.mediaTypeValue + "/"
    if (!this.file.type.includes(expectedMime)) {
      this.showError(this.errorInvalidFormatValue)
      return
    }

    // Mostrar barra de progreso, ocultar form
    this.showProgress()

    try {
      // Step 1: Crear media en Mediastream
      console.log("[vod-upload] Step 1: Creating media...")
      const createResponse = await this.postJSON(this.createMediaUrlValue, {
        media_type: this.mediaTypeValue,
      })
      const vodMediaId = createResponse.vod_media_id
      console.log("[vod-upload] Step 1 done, vodMediaId:", vodMediaId)
      if (!vodMediaId) throw new Error("No se pudo crear el media")

      this.vodMediaIdValue = vodMediaId

      // Step 2: Obtener URL del servidor de chunks
      console.log("[vod-upload] Step 2: Getting upload URL...")
      const uploadResponse = await this.postJSON(this.uploadUrlValue, {
        file_name: this.file.name,
        media_id: vodMediaId,
        size: this.file.size,
      })
      const chunkServerUrl = uploadResponse.url
      console.log("[vod-upload] Step 2 done, chunkServerUrl:", chunkServerUrl)
      if (!chunkServerUrl) throw new Error("No se obtuvo URL de upload")

      // Step 3: Subir chunks secuencialmente
      console.log("[vod-upload] Step 3: Uploading chunks...")
      await this.uploadChunks(chunkServerUrl, vodMediaId)
      console.log("[vod-upload] Step 3 done, all chunks uploaded")

      // Step 4: Exito — cierra modal, muestra "procesando" o "celebracion"
      this.onUploadComplete()
    } catch (error) {
      console.error("[vod-upload] Error:", error)
      this.showUploadError()
    }
  }

  // --- Chunked upload ---

  async uploadChunks(serverUrl, vodMediaId) {
    const chunkSize = this.chunkSizeValue
    const totalChunks = Math.ceil(this.file.size / chunkSize)
    let progressCurrent = -1

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize
      const end = Math.min(start + chunkSize, this.file.size)
      const blob = this.file.slice(start, end)
      const chunkIndex = i + 1 // 1-indexed como el frontend

      // POST chunk directamente a Mediastream
      const formData = new FormData()
      formData.append("start", start)
      formData.append("end", end)
      formData.append("file", blob)

      const response = await fetch(`${serverUrl}/${chunkIndex}`, {
        method: "POST",
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Chunk ${chunkIndex} failed: ${response.status}`)
      }

      // Actualizar progreso
      const percent = Math.ceil((chunkIndex * 100) / totalChunks)
      if (percent !== progressCurrent) {
        progressCurrent = percent
        this.updateProgressUI(percent)

        // Notificar al backend del progreso
        try {
          await this.patchJSON(this.updatePercentUrlValue, { percent })
        } catch (e) {
          console.error("[vod-upload] Error actualizando progreso:", e)
          throw e
        }
      }
    }
  }

  // --- Post-upload ---

  onUploadComplete() {
    this.closeModal()
    this.vodReadyValue = true
    this.vodMediaReadyValue = false

    // Directo a "Estamos casi listos!!!" — sin pantalla de celebracion.
    // El polling detecta cuando Mediastream tiene el player listo
    // y reemplaza automaticamente el partial con el player funcional.
    this.hideAllStates()
    if (this.hasProcessingStateTarget) this.processingStateTarget.classList.remove("hidden")
    this.startPolling()
  }

  // --- Polling ---

  startPolling() {
    this.stopPolling()
    this._polling = true
    this._pollCount = 0
    this._pollNext()
  }

  _pollNext() {
    if (!this._polling) return
    const delay = this._pollCount < 12 ? 10000 : 30000
    this._pollCount++
    this.pollTimer = setTimeout(async () => {
      await this.checkReady()
      if (this._polling) this._pollNext()
    }, delay)
  }

  stopPolling() {
    this._polling = false
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  async checkReady() {
    try {
      const response = await fetch(this.checkReadyUrlValue, {
        headers: { "Accept": "application/json" },
      })
      const data = await response.json()

      if (data.ready) {
        this.stopPolling()
        // Mediastream dice ready — recargar el partial con el player SSR.
        // El ms-audio-player controller tiene reintentos propios si el CDN
        // aun no sirve el asset (puede tardar minutos extra).
        await this.reloadPlayerPartial()
      }
    } catch (e) {
      console.error("[vod-upload] Error verificando estado:", e)
    }
  }

  // --- Player: recarga el partial completo desde el server ---

  async reloadPlayerPartial() {
    try {
      const response = await fetch(this.playerUrlValue, {
        headers: { "Accept": "text/html" },
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const html = await response.text()
      this.element.outerHTML = html
    } catch (e) {
      console.error("[vod-upload] Error recargando player:", e)
      window.location.reload()
    }
  }

  // --- UI Helpers ---

  showProgress() {
    if (this.hasFormSectionTarget) this.formSectionTarget.classList.add("hidden")
    this.hideError()
    if (this.hasProgressSectionTarget) this.progressSectionTarget.classList.remove("hidden")
    this.updateProgressUI(0)
  }

  updateProgressUI(percent) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
  }

  showUploadError() {
    if (this.hasProgressSectionTarget) this.progressSectionTarget.classList.add("hidden")
    if (this.hasFormSectionTarget) this.formSectionTarget.classList.remove("hidden")
    this.showError(this.errorInvalidFormatValue)
  }

  showError(message) {
    if (this.hasErrorSectionTarget) {
      this.errorSectionTarget.textContent = message
      this.errorSectionTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorSectionTarget) {
      this.errorSectionTarget.classList.add("hidden")
      this.errorSectionTarget.textContent = ""
    }
  }

  hideAllStates() {
    if (this.hasNoMediaStateTarget) this.noMediaStateTarget.classList.add("hidden")
    if (this.hasProcessingStateTarget) this.processingStateTarget.classList.add("hidden")
    if (this.hasPlayerStateTarget) this.playerStateTarget.classList.add("hidden")
  }

  closeModal() {
    // Buscar el modal controller mas cercano y cerrarlo
    document.querySelectorAll('[data-manage--modal-target="overlay"]:not(.hidden)').forEach((overlay) => {
      overlay.classList.add("hidden")
      overlay.style.zIndex = ""
      const panel = overlay.querySelector(".modal-panel--open")
      if (panel) panel.classList.remove("modal-panel--open")
    })
    document.body.style.overflow = ""
  }

  // --- HTTP Helpers ---

  async postJSON(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `HTTP ${response.status}`)
    }
    return response.json()
  }

  async patchJSON(url, body) {
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || `HTTP ${response.status}`)
    }
    return response.json()
  }
}
